import { useCallback, useState } from 'react';
import api from 'utils/api';
import type { IdentityDetails, PublishingAnswer, PublishingBatch } from 'types/publishing';

export interface RejectedIsrc {
  isrc: string;
  reason: 'unknown' | 'not_eligible_state' | 'not_mine' | 'already_sent';
}

type Result<T = void> =
  | { ok: true; data: T }
  | { ok: false; message: string; rejected?: RejectedIsrc[] };

const fail = (err: any): Result<never> => ({
  ok: false,
  message: err?.data?.message || err?.message || 'Request failed',
  rejected: err?.data?.rejected,
});

/** Writes for one artist; every call ends with `refetch()` of the view. */
export function usePublishingActions(spotifyArtistId: string | null, refetch: () => unknown) {
  const [isBusy, setBusy] = useState(false);

  const run = useCallback(
    async <T>(fn: () => Promise<T>): Promise<Result<T>> => {
      if (!spotifyArtistId) return { ok: false, message: 'No artist selected' };
      setBusy(true);
      try {
        const data = await fn();
        await refetch();
        return { ok: true, data };
      } catch (err) {
        return fail(err);
      } finally {
        setBusy(false);
      }
    },
    [spotifyArtistId, refetch]
  );

  const createBatch = useCallback(
    (isrcs: string[]) =>
      run<PublishingBatch>(() =>
        api.publishing.createBatch({ id: spotifyArtistId as string, data: { isrcs } })
      ),
    [run, spotifyArtistId]
  );

  const exclude = useCallback(
    (isrc: string) => run(() => api.publishing.mark({ id: `${spotifyArtistId}/tracks/${isrc}` })),
    [run, spotifyArtistId]
  );

  const restore = useCallback(
    (isrcs: string[]) =>
      run(() =>
        Promise.all(
          isrcs.map((isrc) => api.publishing.unmark({ id: `${spotifyArtistId}/tracks/${isrc}` }))
        )
      ),
    [run, spotifyArtistId]
  );

  const answerIdentity = useCallback(
    (answer: PublishingAnswer, details?: IdentityDetails) =>
      run(() =>
        api.publishing.answerIdentity({
          id: spotifyArtistId as string,
          data: { answer, ...details },
        })
      ),
    [run, spotifyArtistId]
  );

  return { createBatch, exclude, restore, answerIdentity, isBusy };
}
